import { Injectable } from '@angular/core';
import { Database, ExamdojoSupabaseService, SupabaseBaseHttpService } from '@examdojo/supabase';
import { from } from 'rxjs';
import { MarksRange, Pagination, QuestionBankFilters, QuestionBankSortOption } from '../app/question-bank.model';
import { QuestionBankHttpModel } from './quetion-bank-http.model';

@Injectable({
  providedIn: 'root',
})
export class QuestionBankHttpService extends SupabaseBaseHttpService<'derivative_questions', 'public'> {
  constructor(supabase: ExamdojoSupabaseService) {
    super(supabase);
  }

  readonly schema = 'public';
  readonly tableName = 'derivative_questions';
  readonly viewName = 'derivative_questions';

  getAll(
    syllabusId: number,
    filters: QuestionBankFilters,
    sortBy: QuestionBankSortOption,
    pagination: Pagination,
    classId?: string,
    courseLevel?: string | null,
  ) {
    const offset = (pagination.page - 1) * pagination.pageSize;
    const params: {
      p_syllabus_id: number;
      p_limit: number;
      p_offset: number;
      p_difficulties?: string[];
      p_completed?: boolean;
      p_types?: Array<Database['public']['Enums']['question_type']>;
      p_topic_level_02_ids?: number[];
      p_topic_match_mode?: 'ANY' | 'ALL';
      p_sort_by?: string;
      p_sort_order?: string;
      p_max_points_min?: number;
      p_max_points_max?: number;
      p_latest_score_min?: number;
      p_latest_score_max?: number;
      p_last_attempt_date_from?: string;
      p_last_attempt_date_to?: string;
      p_tag_ids?: string[];
      p_class_id?: string;
      p_course_level?: string;
    } = {
      p_syllabus_id: syllabusId,
      p_limit: pagination.pageSize,
      p_offset: offset,
    };

    if (filters.completed !== undefined) {
      params.p_completed = filters.completed;
    }

    if (filters.difficulties && filters.difficulties.length > 0) {
      params.p_difficulties = filters.difficulties;
    }

    if (filters.types && filters.types.length > 0) {
      params.p_types = filters.types;
    }

    if (filters.topicIds && filters.topicIds.length > 0) {
      params.p_topic_level_02_ids = filters.topicIds;
    }

    if (filters.topicMatchMode) {
      params.p_topic_match_mode = filters.topicMatchMode;
    }

    if (filters.minPoints !== undefined) {
      params.p_max_points_min = filters.minPoints;
    }

    if (filters.maxPoints !== undefined) {
      params.p_max_points_max = filters.maxPoints;
    }

    if (filters.minLatestScore !== undefined) {
      params.p_latest_score_min = filters.minLatestScore;
    }

    if (filters.maxLatestScore !== undefined) {
      params.p_latest_score_max = filters.maxLatestScore;
    }

    if (filters.lastAttemptDateFrom) {
      const fromDate = new Date(filters.lastAttemptDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      params.p_last_attempt_date_from = fromDate.toISOString();
    }

    if (filters.lastAttemptDateTo) {
      const toDate = new Date(filters.lastAttemptDateTo);
      toDate.setHours(23, 59, 59, 999);
      params.p_last_attempt_date_to = toDate.toISOString();
    }

    if (filters.tags && filters.tags.length > 0) {
      params.p_tag_ids = filters.tags;
    }

    if (classId) {
      params.p_class_id = classId;
    }

    if (courseLevel) {
      params.p_course_level = courseLevel;
    }

    switch (sortBy) {
      case QuestionBankSortOption.MostRelevant:
        params.p_sort_by = 'relevance';
        params.p_sort_order = 'desc';
        break;
      case QuestionBankSortOption.MostMarks:
        params.p_sort_by = 'max_points';
        params.p_sort_order = 'desc';
        break;
      case QuestionBankSortOption.LeastMarks:
        params.p_sort_by = 'max_points';
        params.p_sort_order = 'asc';
        break;
    }

    return from(
      this.supabase.client
        .rpc('search_questions_with_facets', params)
        .throwOnError()
        .then((res) => res.data! as unknown as QuestionBankHttpModel),
    );
  }

  getMarksRange(syllabusId: number) {
    return from(
      Promise.all([
        this.supabase.client
          .from('derivative_questions')
          .select('max_points')
          .eq('syllabus_id', syllabusId)
          .not('max_points', 'is', null)
          .order('max_points', { ascending: true })
          .limit(1)
          .single()
          .then((res) => res.data?.max_points ?? null),
        this.supabase.client
          .from('derivative_questions')
          .select('max_points')
          .eq('syllabus_id', syllabusId)
          .not('max_points', 'is', null)
          .order('max_points', { ascending: false })
          .limit(1)
          .single()
          .then((res) => res.data?.max_points ?? null),
      ]).then(([min, max]): MarksRange => {
        if (min === null || max === null) {
          throw new Error('No marks range found');
        }
        return { min, max };
      }),
    );
  }
}
